import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Button } from '../shared/Button';
import { FormField } from '../shared/FormField';
import { useToast } from '../shared/Toast';
import { QuickActions } from './QuickActions';

/**
 * ChevronDownIcon Component
 */
const ChevronDownIcon = () => (
  <svg
    className="w-5 h-5"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M19 9l-7 7-7-7"
    />
  </svg>
);

/**
 * ChevronUpIcon Component
 */
const ChevronUpIcon = () => (
  <svg
    className="w-5 h-5"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M5 15l7-7 7 7"
    />
  </svg>
);

/**
 * AIAssistant Component
 * Main container for AI-powered caption regeneration features
 * Includes quick actions, custom feedback, and version history
 */
export function AIAssistant({ imageId, currentCaption, onRegenerate }) {
  const { t } = useTranslation('approval');
  const toast = useToast();
  const [isExpanded, setIsExpanded] = useState(false);
  const [customFeedback, setCustomFeedback] = useState('');
  const [feedbackError, setFeedbackError] = useState('');
  const [isRegenerating, setIsRegenerating] = useState(false);

  /**
   * Validate custom feedback
   * @param {string} text - The feedback text to validate
   * @returns {boolean} - Whether the feedback is valid
   */
  const validateFeedback = (text) => {
    if (text.length > 0 && text.length < 5) {
      setFeedbackError(t('aiAssistant.validation.minLength'));
      return false;
    }
    if (text.length > 500) {
      setFeedbackError(t('aiAssistant.validation.maxLength'));
      return false;
    }
    setFeedbackError('');
    return true;
  };

  /**
   * Handle custom feedback change
   * @param {Event} e - The input event
   */
  const handleFeedbackChange = (e) => {
    const text = e.target.value;
    setCustomFeedback(text);
    validateFeedback(text);
  };

  /**
   * Handle custom feedback regeneration
   */
  const handleCustomRegenerate = async () => {
    if (!validateFeedback(customFeedback) || customFeedback.length === 0) {
      setFeedbackError(t('aiAssistant.validation.required'));
      return;
    }

    setIsRegenerating(true);

    try {
      await onRegenerate({
        feedback: customFeedback,
        aiRequest: {
          feedback: customFeedback,
          temperature: 0.8
        }
      });

      toast.success(t('aiAssistant.toast.regenerating'));
      setCustomFeedback(''); // Clear feedback after successful submission
    } catch (error) {
      console.error('Error regenerating with custom feedback:', error);
      toast.error(t('aiAssistant.toast.error'));
    } finally {
      setIsRegenerating(false);
    }
  };

  /**
   * Handle quick action regeneration from QuickActions component
   * @param {Object} params - Parameters for regeneration
   */
  const handleQuickRegenerate = async ({ feedback, temperature }) => {
    try {
      await onRegenerate({
        feedback,
        aiRequest: {
          feedback,
          temperature
        }
      });
    } catch (error) {
      throw error; // Let QuickActions handle the error
    }
  };

  return (
    <div className="border border-gray-200 rounded-lg bg-gray-50">
      {/* Header - Always visible */}
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-100 transition-colors rounded-t-lg"
        aria-expanded={isExpanded}
        aria-controls="ai-assistant-content"
      >
        <div className="flex items-center gap-2">
          <span className="text-lg" role="img" aria-label={t('aiAssistant.title')}>
            🤖
          </span>
          <span className="font-medium text-gray-900">
            {t('aiAssistant.title')}
          </span>
        </div>

        <div className="text-gray-500">
          {isExpanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
        </div>
      </button>

      {/* Expandable Content */}
      {isExpanded && (
        <div
          id="ai-assistant-content"
          className="px-4 pb-4 space-y-6 border-t border-gray-200"
        >
          {/* Quick Actions Section */}
          <div className="pt-4">
            <QuickActions
              imageId={imageId}
              onRegenerate={handleQuickRegenerate}
            />
          </div>

          {/* Custom Feedback Section */}
          <div className="space-y-3">
            <h4 className="text-sm font-medium text-gray-700">
              {t('aiAssistant.feedback.title')}
            </h4>

            <FormField
              label=""
              error={feedbackError}
              helperText={!feedbackError ? t('aiAssistant.feedback.counter', { count: customFeedback.length }) : undefined}
            >
              <textarea
                value={customFeedback}
                onChange={handleFeedbackChange}
                placeholder={t('aiAssistant.feedback.placeholder')}
                className={`
                  w-full px-3 py-2 border rounded-lg resize-none
                  focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
                  ${feedbackError ? 'border-red-300' : 'border-gray-300'}
                `}
                rows={3}
                maxLength={500}
                aria-label={t('aiAssistant.feedback.title')}
                aria-invalid={!!feedbackError}
                aria-describedby={feedbackError ? 'feedback-error' : 'feedback-helper'}
              />
            </FormField>

            <Button
              variant="primary"
              size="md"
              onClick={handleCustomRegenerate}
              loading={isRegenerating}
              disabled={!customFeedback || !!feedbackError || customFeedback.length < 5}
              className="w-full sm:w-auto"
            >
              {t('aiAssistant.button.regenerate')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// PropTypes validation
AIAssistant.propTypes = {
  imageId: PropTypes.string.isRequired,
  currentCaption: PropTypes.string.isRequired,
  onRegenerate: PropTypes.func.isRequired
};

export default AIAssistant;